import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactGA from "react-ga4";

import { hidePollPopup, showPollPopup } from "../App/actions";
import { api, checkEmptyObject, localStorage, notification } from "../../services";
import { LocalStorageKey } from "../../models";
import { selectUserInfo } from "../../routes/Auth/auth.ducks";

const POLL_GA_CATEGORY = "Stranica Prosječnih Cijena Nekretnina";

// Give the user a moment with the chart before asking about it.
const POLL_DELAY_MS = 10000;

const POLL_ID = "real-estate-prices";

// Closing the poll three times in a row reads as "not interested", so stop
// asking for a while. Answering it breaks the streak.
const POLL_DISMISS_LIMIT = 3;
const POLL_DISMISS_SNOOZE_MS = 15 * 24 * 60 * 60 * 1000;

// Once we have their answer there is nothing left to ask for a good while.
const POLL_ANSWERED_SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

interface PollDismissalI {
  count: number;
  snoozeUntil?: number;
}

const readDismissals = (): Record<string, PollDismissalI> =>
  localStorage.getItem(LocalStorageKey.pollPopupDismissals) || {};

const readDismissal = (pollId: string): PollDismissalI =>
  readDismissals()[pollId] || { count: 0 };

const writeDismissal = (pollId: string, dismissal: PollDismissalI) => {
  localStorage.setItem(LocalStorageKey.pollPopupDismissals, {
    ...readDismissals(),
    [pollId]: dismissal
  });
};

const isSnoozed = (pollId: string) => {
  const { snoozeUntil } = readDismissal(pollId);
  return !!snoozeUntil && Date.now() < snoozeUntil;
};

const registerDismissal = (pollId: string) => {
  const nextCount = readDismissal(pollId).count + 1;

  if (nextCount >= POLL_DISMISS_LIMIT) {
    // Start the streak over, so the snooze expiring gives another three tries.
    writeDismissal(pollId, {
      count: 0,
      snoozeUntil: Date.now() + POLL_DISMISS_SNOOZE_MS
    });
    return;
  }

  writeDismissal(pollId, { count: nextCount });
};

const registerAnswer = (pollId: string) => {
  writeDismissal(pollId, { count: 0 });
};

const registerSubmission = (pollId: string) => {
  writeDismissal(pollId, {
    count: 0,
    snoozeUntil: Date.now() + POLL_ANSWERED_SNOOZE_MS
  });
};

const trackPoll = (label: string) => {
  ReactGA.event({
    category: POLL_GA_CATEGORY,
    action: "Click",
    label
  });
};

// Shows the average-prices poll once the chart has been on screen for
// POLL_DELAY_MS, and takes it down again when the page navigates away.
export const useResultsPollPopup = (isChartVisible: boolean) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUserInfo)

  useEffect(() => {
    const userIsLoggedIn = Boolean(checkEmptyObject(user))
    const userParticipatesInSurveys = user && user.participateInSurveys
    const loggedInUserDoesntParticipateInSurveys = userIsLoggedIn && !userParticipatesInSurveys

    if (!isChartVisible || isSnoozed(POLL_ID) || loggedInUserDoesntParticipateInSurveys) {
      return;
    };

    const timeoutId = setTimeout(() => {
      trackPoll("Prikazana anketa")

      dispatch(
        showPollPopup({
          pollId: POLL_ID,
          title: "Jesu li vam podaci o kretanju cijena korisni?",
          yesAnswer: {
            text: "Da",
            onClick: () => {
              trackPoll("Zadovoljan korisnik");
              registerAnswer(POLL_ID);
            },
            followUp: {
              title: "Što vam je najkorisnije u ovom pregledu?",
              commentPlaceholder: "Želite li nam reći nešto više?",
              options: [
                { value: 1, label: "Prosječna cijena po m²" },
                { value: 2, label: "Kretanje cijena kroz vrijeme" },
                { value: 3, label: "Raspon cijena" },
                { value: 4, label: "Broj oglasa uključenih u izračun" },
                { value: 5, label: "Usporedba lokacija" },
                { value: 6, label: "Nešto drugo" }
              ]
            }
          },
          noAnswer: {
            text: "Ne, potrebno je poboljšanje",
            onClick: () => {
              trackPoll("Nezadovoljan korisnik");
              registerAnswer(POLL_ID);
            },
            followUp: {
              title: "Što bismo trebali poboljšati da bi vam ovaj pregled bio korisniji?",
              commentPlaceholder: "Napišite što vam nedostaje ili što bismo mogli poboljšati.",
              options: [
                { value: 1, label: "Više detalja o cijenama" },
                { value: 2, label: "Preciznije podatke za lokaciju" },
                { value: 3, label: "Lakšu usporedbu lokacija" },
                { value: 4, label: "Više mogućnosti filtriranja" },
                { value: 5, label: "Jasniji prikaz podataka" },
                { value: 6, label: "Nešto drugo" }
              ]
            }
          },
          onCancel: () => {
            trackPoll("Zatvorena anketa");
            registerDismissal(POLL_ID);
          },
          onSubmit: async ({ isYes, options, comment }) => {
            try {
              await api.Survey.realEstateFeedback({
                isUseful: isYes,
                ...(isYes ? {
                  usefulReasons: options,
                  commentIfUseful: comment
                } : {
                  notUsefulReasons: options,
                  commentIfNotUseful: comment

                })
              });
              registerSubmission(POLL_ID);
              notification.show("Hvala na povratnoj informaciji!");

            } catch (error) {

            }
          }
        })
      );
    }, POLL_DELAY_MS);

    return () => {
      clearTimeout(timeoutId);
      dispatch(hidePollPopup());
    };
  }, [isChartVisible]);
};



// WEBPACK FOOTER //
// ./src/containers/RealEstatePricesPage/useResultsPollPopup.ts